import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ESDM_DOMAINS,
  ESDM_ITEMS,
  ESDM_LEVEL_AGE_BANDS,
  calculateAgeInMonths,
  calculateCorrectedAgeInMonths,
  getEsdmDomainRollup,
} from '@aba-denver/core';
import type { EsdmDomainRollup, EsdmLevel } from '@aba-denver/core';
import { useData } from '../contexts/DataContext';
import { patientAgeLabel, formatDate } from '../lib/format';

const LEVELS: EsdmLevel[] = [1, 2, 3, 4];

/** One color per nível — one radar line each. */
const LEVEL_COLOR: Record<EsdmLevel, string> = {
  1: 'var(--primary)',
  2: 'var(--secondary)',
  3: 'var(--success)',
  4: 'var(--warning)',
};

const pctLabel = (acquired: number, total: number) =>
  total > 0 ? `${Math.round((acquired / total) * 100)}%` : '—';

/** Age band (in months) each ESDM nível targets — mirrors ESDM_LEVEL_AGE_BANDS. */
const LEVEL_MONTHS: Record<EsdmLevel, [number, number]> = {
  1: [12, 18],
  2: [18, 24],
  3: [24, 36],
  4: [36, 48],
};

/**
 * How much of a nível a child of `ageMonths` is expected to have mastered:
 * 0 before the band starts, 1 once past it, linear within the band.
 */
function expectedFractionForLevel(level: EsdmLevel, ageMonths: number): number {
  const [lo, hi] = LEVEL_MONTHS[level];
  if (ageMonths >= hi) return 1;
  if (ageMonths <= lo) return 0;
  return (ageMonths - lo) / (hi - lo);
}

// ---- "Copiar gráfico" — rasterize the radar + title + legends to a PNG ----

interface ChartExportMeta {
  title: string;
  subtitle: string;
  levelLegend: { label: string; color: string }[];
  expectedLabel: string;
  codes: { code: string; name: string }[];
}

const EXPORT_CSS_VARS = [
  '--primary',
  '--secondary',
  '--success',
  '--warning',
  '--border',
  '--card',
  '--text',
  '--text-secondary',
] as const;

/** Inline `var(--token)` → its computed value so the SVG renders standalone. */
function resolveCssVars(markup: string): string {
  const cs = getComputedStyle(document.documentElement);
  const map: Record<string, string> = {};
  for (const v of EXPORT_CSS_VARS) map[v] = cs.getPropertyValue(v).trim() || '#000';
  return markup.replace(/var\((--[a-z0-9-]+)\)/gi, (_, name) => map[name] ?? '#000');
}

const xmlEsc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

async function renderChartPng(sourceSvg: SVGSVGElement, meta: ChartExportMeta): Promise<Blob> {
  const PAD = 24;
  const CHART = 512;
  const W = CHART + PAD * 2;
  const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  const parts: string[] = [];
  let y = PAD;

  parts.push(
    `<text x="${PAD}" y="${y + 16}" font-size="18" font-weight="700" fill="var(--text)">${xmlEsc(
      meta.title
    )}</text>`
  );
  if (meta.subtitle) {
    parts.push(
      `<text x="${PAD}" y="${y + 36}" font-size="12" fill="var(--text-secondary)">${xmlEsc(
        meta.subtitle
      )}</text>`
    );
  }
  y += 52;

  parts.push(
    `<svg x="${PAD}" y="${y}" width="${CHART}" height="${CHART}" viewBox="${sourceSvg.getAttribute(
      'viewBox'
    )}">${sourceSvg.innerHTML}</svg>`
  );
  y += CHART + 16;

  const legend = [
    ...meta.levelLegend.map((e) => ({ ...e, dashed: false })),
    { label: meta.expectedLabel, color: 'var(--text)', dashed: true },
  ];
  const cols = 2;
  const colW = (W - PAD * 2) / cols;
  legend.forEach((e, i) => {
    const lx = PAD + (i % cols) * colW;
    const ly = y + Math.floor(i / cols) * 22;
    parts.push(
      e.dashed
        ? `<line x1="${lx}" y1="${ly + 3}" x2="${lx + 20}" y2="${ly + 3}" stroke="${e.color}" stroke-width="2" stroke-dasharray="5 3"/>`
        : `<rect x="${lx}" y="${ly - 6}" width="13" height="13" rx="3" fill="${e.color}"/>`
    );
    parts.push(
      `<text x="${lx + 22}" y="${ly + 3}" font-size="12" fill="var(--text)">${xmlEsc(e.label)}</text>`
    );
  });
  y += Math.ceil(legend.length / cols) * 22 + 14;

  const ccolW = (W - PAD * 2) / 2;
  meta.codes.forEach((c, i) => {
    const cx = PAD + (i % 2) * ccolW;
    const cy = y + Math.floor(i / 2) * 17 + 10;
    const name = c.name.length > 42 ? `${c.name.slice(0, 41)}…` : c.name;
    parts.push(
      `<text x="${cx}" y="${cy}" font-size="11" fill="var(--text-secondary)"><tspan font-weight="700" fill="var(--text)">${xmlEsc(
        c.code
      )}</tspan> — ${xmlEsc(name)}</text>`
    );
  });
  y += Math.ceil(meta.codes.length / 2) * 17 + 10 + PAD;

  const H = y;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" font-family='${FONT}'>` +
    `<rect width="100%" height="100%" fill="#ffffff"/>` +
    resolveCssVars(parts.join('')) +
    `</svg>`;

  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Falha ao renderizar o SVG'));
      img.src = url;
    });
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = W * scale;
    canvas.height = H * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponível');
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao gerar PNG'))), 'image/png')
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Radar chart: one axis per habilidade, one closed line per nível (% adquirido)
 * plus a dashed reference line with the acquisition expected for the child's age.
 * A nível line spans only the axes where that nível has items (the curriculum is
 * sparse); the expected line spans every axis (0 = not expected yet).
 */
function ConsolidadoRadar({
  axes,
  cell,
  expected,
  svgRef,
}: {
  axes: { code: string; name: string }[];
  cell: Map<string, EsdmDomainRollup>;
  expected: Map<string, number>;
  svgRef?: React.Ref<SVGSVGElement>;
}) {
  const n = axes.length;
  if (n < 3) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        Gráfico radar disponível a partir de 3 habilidades avaliadas.
      </p>
    );
  }

  const CX = 210;
  const CY = 210;
  const R = 180;
  const RINGS = [25, 50, 75, 100];
  const angle = (i: number) => (2 * Math.PI * i) / n - Math.PI / 2;
  const xy = (i: number, value: number): [number, number] => {
    const r = (value / 100) * R;
    return [CX + r * Math.cos(angle(i)), CY + r * Math.sin(angle(i))];
  };
  const ptStr = (pts: [number, number][]) => pts.map((p) => `${p[0]},${p[1]}`).join(' ');

  return (
    <svg
      ref={svgRef}
      viewBox="-28 -28 476 476"
      style={{ width: '100%', display: 'block' }}
      role="img"
      aria-label="Gráfico radar de aquisição por habilidade e nível"
    >
      {RINGS.map((ring) => (
        <polygon
          key={ring}
          points={ptStr(axes.map((_, i) => xy(i, ring)))}
          fill="none"
          stroke="var(--border)"
        />
      ))}

      {RINGS.map((ring) => (
        <text
          key={`scale-${ring}`}
          x={CX + 4}
          y={CY - (ring / 100) * R}
          fontSize={9}
          fill="var(--text-secondary)"
          dominantBaseline="middle"
          stroke="var(--card)"
          strokeWidth={3}
          paintOrder="stroke"
        >
          {ring}%
        </text>
      ))}

      {axes.map((ax, i) => {
        const [x, y] = xy(i, 100);
        const [lx, ly] = xy(i, 113);
        const cos = Math.cos(angle(i));
        const anchor = Math.abs(cos) < 0.35 ? 'middle' : cos > 0 ? 'start' : 'end';
        return (
          <g key={ax.code}>
            <line x1={CX} y1={CY} x2={x} y2={y} stroke="var(--border)" />
            <text
              x={lx}
              y={ly}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontSize={10}
              fontWeight={700}
              fill="var(--text-secondary)"
            >
              {ax.code}
            </text>
          </g>
        );
      })}

      {LEVELS.map((level) => {
        const pts = axes
          .map((ax, i) => {
            const c = cell.get(`${level}:${ax.code}`);
            if (!c || c.total === 0) return null;
            return xy(i, Math.round(c.pctAcquired * 100));
          })
          .filter((p): p is [number, number] => p !== null);

        if (pts.length === 0) return null;
        const color = LEVEL_COLOR[level];

        return (
          <g key={level}>
            {pts.length >= 3 ? (
              <polygon points={ptStr(pts)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
            ) : pts.length === 2 ? (
              <polyline points={ptStr(pts)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
            ) : null}
            {pts.map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill={color} />
            ))}
          </g>
        );
      })}

      {/* Expected-for-age reference line — dashed, spans every axis. */}
      <polygon
        points={ptStr(axes.map((ax, i) => xy(i, Math.round(expected.get(ax.code) ?? 0))))}
        fill="none"
        stroke="var(--text)"
        strokeWidth={2}
        strokeDasharray="7 4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RelatorioConsolidado() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getChildById, getChildEsdmProgress } = useData();
  const radarRef = useRef<SVGSVGElement | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'done' | 'error'>('idle');

  const child = id ? getChildById(id) : undefined;

  if (!child) {
    return (
      <div className="empty-state">
        Paciente não encontrado.{' '}
        <a onClick={() => navigate('/relatorios')} style={{ cursor: 'pointer' }}>
          Voltar
        </a>
      </div>
    );
  }

  const progressDoc = getChildEsdmProgress(child.id);
  const rollup = getEsdmDomainRollup(ESDM_ITEMS, progressDoc?.items ?? {});

  const cell = new Map<string, EsdmDomainRollup>();
  for (const r of rollup) cell.set(`${r.level}:${r.domain}`, r);

  // Rows = the canonical domain list, keeping only domains that carry items in
  // at least one nível (the matrix is sparse — 43 of 60 cells have items).
  const rows = ESDM_DOMAINS.filter((d) => LEVELS.some((lvl) => cell.has(`${lvl}:${d.code}`)));

  // Acquisition expected for the child's (corrected, if premature) age: per
  // domain, the item-count-weighted mean of each nível's expected fraction.
  const ageMonths =
    child.isPremature && child.gestationalWeeks
      ? calculateCorrectedAgeInMonths(child.birthDate, child.gestationalWeeks)
      : calculateAgeInMonths(child.birthDate);
  const expectedByDomain = new Map<string, number>();
  for (const d of rows) {
    let weighted = 0;
    let items = 0;
    for (const level of LEVELS) {
      const r = cell.get(`${level}:${d.code}`);
      if (!r) continue;
      weighted += expectedFractionForLevel(level, ageMonths) * r.total;
      items += r.total;
    }
    expectedByDomain.set(d.code, items > 0 ? (weighted / items) * 100 : 0);
  }

  const columnTotals = LEVELS.map((level) => {
    const rs = rollup.filter((r) => r.level === level);
    return {
      level,
      acquired: rs.reduce((s, r) => s + r.acquired, 0),
      total: rs.reduce((s, r) => s + r.total, 0),
    };
  });

  const nothingFilled = rollup.every((r) => r.filled === 0);
  const canCopyChart = rows.length >= 3;

  const handleCopyChart = async () => {
    if (!radarRef.current) return;
    setCopyState('copying');
    const meta: ChartExportMeta = {
      title: `Consolidado — ${child.name}`,
      subtitle: `${patientAgeLabel(child)}${
        progressDoc ? ` · atualizado em ${formatDate(progressDoc.updatedAt)}` : ''
      }`,
      levelLegend: LEVELS.map((l) => ({ label: `Nível ${l}`, color: LEVEL_COLOR[l] })),
      expectedLabel: `Esperado p/ idade (${Math.round(ageMonths)} meses)`,
      codes: rows.map((d) => ({ code: d.code, name: d.name })),
    };
    const filename = `consolidado-${child.name.replace(/\s+/g, '-').toLowerCase()}.png`;
    try {
      const canWriteImage =
        typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
      if (canWriteImage) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': renderChartPng(radarRef.current, meta) }),
        ]);
      } else {
        downloadBlob(await renderChartPng(radarRef.current, meta), filename);
      }
      setCopyState('done');
    } catch (err) {
      console.error(err);
      try {
        downloadBlob(await renderChartPng(radarRef.current, meta), filename);
        setCopyState('done');
      } catch (err2) {
        console.error(err2);
        setCopyState('error');
      }
    }
    window.setTimeout(() => setCopyState('idle'), 2500);
  };

  const copyLabel =
    copyState === 'copying'
      ? 'Copiando…'
      : copyState === 'done'
        ? '✓ Copiado'
        : copyState === 'error'
          ? 'Falhou — tentar de novo'
          : 'Copiar gráfico';

  return (
    <div>
      <div className="page-header">
        <div>
          <a onClick={() => navigate('/relatorios')} style={{ cursor: 'pointer', fontSize: 13 }}>
            ‹ Relatórios
          </a>
          <h1 style={{ marginTop: 6 }}>Consolidado — {child.name}</h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {patientAgeLabel(child)}
            {progressDoc && ` · atualizado em ${formatDate(progressDoc.updatedAt)}`}
          </p>
        </div>
        <button
          className="btn secondary"
          onClick={() => navigate(`/patients/${child.id}/esdm/report`)}
        >
          Ver resumo
        </button>
      </div>

      {nothingFilled ? (
        <div className="card">
          <div className="empty-state">Nenhum item preenchido para este paciente ainda.</div>
        </div>
      ) : (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>Aquisição por habilidade e nível</h2>
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
              % de itens adquiridos em cada domínio, por nível.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ minWidth: 640 }}>
                <thead>
                  <tr>
                    <th>Habilidade</th>
                    {LEVELS.map((level) => (
                      <th key={level} style={{ textAlign: 'center' }}>
                        Nível {level}
                        <div className="muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                          {ESDM_LEVEL_AGE_BANDS[level]}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.code}>
                      <td style={{ fontWeight: 600 }}>{d.name}</td>
                      {LEVELS.map((level) => {
                        const r = cell.get(`${level}:${d.code}`);
                        if (!r || r.total === 0) {
                          return (
                            <td key={level} style={{ textAlign: 'center' }} className="muted">
                              —
                            </td>
                          );
                        }
                        const pct = Math.round(r.pctAcquired * 100);
                        return (
                          <td
                            key={level}
                            style={{
                              textAlign: 'center',
                              fontWeight: 700,
                              background: `color-mix(in srgb, var(--primary) ${pct}%, transparent)`,
                            }}
                            title={`${r.acquired} de ${r.total} itens adquiridos`}
                          >
                            {pct}%
                            <div className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
                              {r.acquired}/{r.total}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 800 }}>TOTAL</td>
                    {columnTotals.map(({ level, acquired, total }) => (
                      <td key={level} style={{ textAlign: 'center', fontWeight: 800 }}>
                        {pctLabel(acquired, total)}
                        <div className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
                          {acquired}/{total}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}
            >
              <h2 style={{ margin: 0 }}>Consolidado – 4 Níveis</h2>
              {canCopyChart && (
                <button
                  className="btn secondary sm"
                  onClick={() => void handleCopyChart()}
                  disabled={copyState === 'copying'}
                >
                  {copyLabel}
                </button>
              )}
            </div>

            <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              {LEVELS.map((level) => (
                <div key={level} className="row" style={{ gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: LEVEL_COLOR[level],
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: 13 }}>Nível {level}</span>
                </div>
              ))}
              <div className="row" style={{ gap: 6 }}>
                <span
                  style={{
                    width: 18,
                    borderTop: '2px dashed var(--text)',
                    display: 'inline-block',
                  }}
                />
                <span style={{ fontSize: 13 }}>
                  Esperado p/ idade ({Math.round(ageMonths)} meses)
                </span>
              </div>
            </div>

            <ConsolidadoRadar axes={rows} cell={cell} expected={expectedByDomain} svgRef={radarRef} />

            <div
              className="grid"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 4,
                marginTop: 20,
              }}
            >
              {rows.map((d) => (
                <div key={d.code} className="muted" style={{ fontSize: 12 }}>
                  <strong style={{ color: 'var(--text)' }}>{d.code}</strong> — {d.name}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
